/**
 * OLVRateContext
 *
 * Provides the live OLV token exchange rate from Firestore.
 * This rate is used across the application for:
 * - Converting USD costs to OLV tokens
 * - Displaying current exchange rates
 * - Transaction history calculations
 *
 * The rate is fetched from: global_olv_token/token_price
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { doc, onSnapshot, getDocFromServer } from 'firebase/firestore';
import db from '../utils/firestore';

// ============================================================================
// Types
// ============================================================================

interface OLVRateState {
  /** Current OLV token price in USD */
  rateUsd: number | null;
  /** Whether the rate is currently loading */
  isLoading: boolean;
  /** Error message if rate fetch failed */
  error: string | null;
  /** Timestamp of last successful update */
  lastUpdated: number | null;
}

interface OLVRateContextValue {
  state: OLVRateState;
  /** Get the effective rate (live rate or fallback) */
  getEffectiveRate: () => number;
  /** Convert USD to OLV tokens using the current rate */
  usdToOlv: (usd: number) => number;
  /** Convert OLV tokens to USD using the current rate */
  olvToUsd: (olv: number) => number;
  /** Force refresh the rate from Firestore */
  refresh: () => Promise<void>;
}

// ============================================================================
// Constants
// ============================================================================

/** Fallback rate when Firestore is unavailable */
const FALLBACK_RATE_USD = 0.001;

/** Firestore collection and document path */
const FIRESTORE_COLLECTION = 'global_olv_token';
const FIRESTORE_DOCUMENT = 'token_price';

// ============================================================================
// Context
// ============================================================================

const OLVRateContext = createContext<OLVRateContextValue | null>(null);

export function OLVRateProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<OLVRateState>({
    rateUsd: null,
    isLoading: true,
    error: null,
    lastUpdated: null,
  });

  // Set up real-time listener for OLV rate
  useEffect(() => {
    const ref = doc(db, FIRESTORE_COLLECTION, FIRESTORE_DOCUMENT);

    // Initial fetch with getDocFromServer for freshness
    const fetchInitial = async () => {
      try {
        const snapshot = await getDocFromServer(ref);
        if (snapshot.exists()) {
          const data = snapshot.data();
          const raw = data.token_price !== undefined ? data.token_price : data.price;
          const parsed = typeof raw === 'number' ? raw : parseFloat(String(raw));

          if (!isNaN(parsed) && isFinite(parsed) && parsed > 0) {
            setState({
              rateUsd: parsed,
              isLoading: false,
              error: null,
              lastUpdated: Date.now(),
            });
          } else {
            console.warn('[OLVRateContext] Invalid rate value:', raw);
            setState(prev => ({
              ...prev,
              isLoading: false,
              error: 'invalid rate',
            }));
          }
        } else {
          console.warn('[OLVRateContext] Token price document does not exist');
          setState(prev => ({
            ...prev,
            isLoading: false,
            error: 'unavailable',
          }));
        }
      } catch (err) {
        console.error('[OLVRateContext] Failed to fetch initial rate:', err);
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: 'fetch failed',
        }));
      }
    };

    fetchInitial();

    // Set up real-time listener for subsequent updates
    const unsubscribe = onSnapshot(
      ref,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          const raw = data.token_price !== undefined ? data.token_price : data.price;
          const parsed = typeof raw === 'number' ? raw : parseFloat(String(raw));

          if (!isNaN(parsed) && isFinite(parsed) && parsed > 0) {
            setState({
              rateUsd: parsed,
              isLoading: false,
              error: null,
              lastUpdated: Date.now(),
            });
          }
        }
      },
      (err) => {
        console.error('[OLVRateContext] Snapshot listener error:', err);
        setState(prev => ({
          ...prev,
          error: 'listener error',
        }));
      }
    );

    return () => unsubscribe();
  }, []);

  /**
   * Get the effective rate (live rate or fallback)
   */
  const getEffectiveRate = useCallback((): number => {
    return (typeof state.rateUsd === 'number' && isFinite(state.rateUsd) && state.rateUsd > 0)
      ? state.rateUsd
      : FALLBACK_RATE_USD;
  }, [state.rateUsd]);

  /**
   * Convert USD to OLV tokens (returns precise fractional value)
   */
  const usdToOlv = useCallback((usd: number): number => {
    const rate = getEffectiveRate();
    return rate > 0 ? usd / rate : 0;
  }, [getEffectiveRate]);

  /**
   * Convert OLV tokens to USD
   */
  const olvToUsd = useCallback((olv: number): number => {
    const rate = getEffectiveRate();
    return rate > 0 ? olv * rate : 0;
  }, [getEffectiveRate]);

  /**
   * Force refresh the rate from Firestore
   */
  const refresh = useCallback(async (): Promise<void> => {
    const ref = doc(db, FIRESTORE_COLLECTION, FIRESTORE_DOCUMENT);
    try {
      const snapshot = await getDocFromServer(ref);
      if (snapshot.exists()) {
        const data = snapshot.data();
        const raw = data.token_price !== undefined ? data.token_price : data.price;
        const parsed = typeof raw === 'number' ? raw : parseFloat(String(raw));

        if (!isNaN(parsed) && isFinite(parsed) && parsed > 0) {
          setState({
            rateUsd: parsed,
            isLoading: false,
            error: null,
            lastUpdated: Date.now(),
          });
        }
      }
    } catch (err) {
      console.error('[OLVRateContext] Refresh failed:', err);
    }
  }, []);

  const contextValue: OLVRateContextValue = {
    state,
    getEffectiveRate,
    usdToOlv,
    olvToUsd,
    refresh,
  };

  return (
    <OLVRateContext.Provider value={contextValue}>
      {children}
    </OLVRateContext.Provider>
  );
}

/**
 * Hook to access the OLV rate context
 */
export function useOLVRate(): OLVRateContextValue {
  const context = useContext(OLVRateContext);
  if (!context) {
    throw new Error('useOLVRate must be used within an OLVRateProvider');
  }
  return context;
}

/**
 * Hook that safely provides OLV rate without throwing
 * Use this in components that may not be wrapped in OLVRateProvider
 */
export function useMaybeOLVRate(): OLVRateContextValue | null {
  return useContext(OLVRateContext);
}

export default OLVRateContext;
